import { getCurrentUser } from "@service/user";
import { getCookie } from "@util/cookie";
import { createContext, useContext, useEffect, useState } from "react";

interface UserInfo {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  country_code?: string;
  role?: string;
  username: string
  is_connected_google: boolean
  profile_picture_path: string
  referral_code?: string

  // HR
  company_id: string
  company_description: string
  company_linkedin: string
  company_logo: string
  company_name: string
  company_size: string
  company_subdomain: string
  company_referrer_code: string
  level: string
  permission: string

  organization_role?: string; // Added for organization role
  company_type?: string; // e.g. "training-agent" — used to gate Training nav
  job_collaborations?: string[]; // Added for job collaborators
  // AI interview engine for this HR user's company (#2810), surfaced by
  // GET /auth/me. "pipecat"/null → v1; "elevenlabs" → v2. Lets HR surfaces
  // pick the engine synchronously (no company-info fetch / race).
  interview_engine?: "pipecat" | "elevenlabs" | null;
  // Drive-thru demo workspaces (company_type == "drive-thru") — which brand's
  // POS UI to show. Branding itself comes from GET /drive-thru/config; the BE
  // scopes all drive-thru data to this brand server-side.
  drive_thru_brand?: "tealive" | "baskbear";
  // Whether "Maya" (the WhatsApp candidate chatbot) is enabled for this HR's
  // company (GET /auth/me). Gates the Maya/WhatsApp surfaces: the job-form
  // phone-call toggle and the candidate drawer's Messages tab.
  maya_enabled?: boolean;
  /**
   * Which merchant a WhatsApp AI Receptionist client belongs to
   * (ourteam-backend#4670). Present ONLY for role "receptionist-client";
   * absent for receptionist-staff (no single merchant) and for every other
   * user. Read it null-safely — never assume it is there.
   */
  receptionist?: { merchantId: string; merchantName: string };
}

const UserProfileContext = createContext<[boolean, UserInfo | undefined]>([
  true,
  undefined,
]);

export const useUserProfileContext = () => {
  return useContext(UserProfileContext);
};

interface Props {
  children: React.ReactNode;
}

function UserProfileProvider(props: Props) {
  // Skip the async loading state entirely when there's no token to validate.
  // This prevents a spurious spinner→login flicker on every page load when unauthenticated.
  const [isLoading, setLoading] = useState<boolean>(() => !!getCookie("authToken"));
  const [userInfo, setUserInfo] = useState<UserInfo | undefined>(undefined);

  useEffect(() => {
    getCurrentUser()
      .then((resp) => {
        setLoading(false);
        if (!resp) {
          setUserInfo(undefined);
          return;
        }

        setUserInfo({
          id: resp.data?.data.id || "",
          email: resp.data?.data.email || "",
          first_name: resp.data?.data.first_name,
          last_name: resp.data?.data.last_name,
          role: resp.data?.data.role,
          username: resp.data?.data.username || "",
          is_connected_google: resp.data?.data.is_connected_google || false,
          profile_picture_path: resp.data?.data.profile_picture_path || "",
          ...resp.data?.data,
        } as UserInfo);
      })
      .catch((err: Error) => {
        setLoading(false);
        setUserInfo(undefined)
        console.log(err?.message || "Failed to get user data");
      });
  }, []);

  return (
    <UserProfileContext.Provider value={[isLoading, userInfo]}>
      {props.children}
    </UserProfileContext.Provider>
  );
}

export default UserProfileProvider;
